/* eslint-disable max-statements */
import React from 'react';
import { BlueprintInstanceType } from '@rta/rta-blueprint-library';
import { Layer } from '@r-core/types';

import { addContextMenu } from '../../context-menu';
import { convertRect2Vectors } from '@r-core/helpers';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';

export type RenderBacksplashPanelsOverheadProps = {
	countertop: any;
	island: any;
	showCountertops: boolean;
	showBacksplashes?: boolean;
};

/**
 * Renders backsplash panels for an island's countertop in overhead view.
 * Handles complex positioning calculations including depth offsets and panel angle adjustments.
 *
 * Each backsplash is positioned:
 * 1. On the inside edge of the countertop (flush with panel inner edge)
 * 2. With proper offset for asymmetric overhangs (countertopCenterOffset)
 * 3. With additional offset for corner backsplash interactions (adjoiningBacksplashOffset)
 *
 * @param props - Component props
 * @param props.countertop - The countertop containing backsplashes
 * @param props.island - The island containing the panels
 * @param props.showCountertops - Whether countertops are currently visible
 * @returns Array of React elements representing backsplash panels
 */
export const RenderBacksplashPanelsOverhead = ({ countertop, island, showCountertops, showBacksplashes = true }: RenderBacksplashPanelsOverheadProps): React.ReactElement[] => {
	if (!showCountertops || !showBacksplashes || !countertop.backsplashes?.children) {
		return [];
	}

	const backsplashFill = 'blue';
	const backsplashOpacity = 0.6;

	return countertop.backsplashes.children.map((backsplash) => {
		const associatedPanel = island.panels.findById(backsplash.associatedPanelId);
		if (!associatedPanel) return null;

		const panelCenter = associatedPanel.center;
		const panelDepth = associatedPanel.thickness;
		const panelOverhang = associatedPanel.countertopOverhang;
		const backsplashDepth = backsplash.size.depth;
		const panelAngle = associatedPanel.getOriginAngle();

		const rawBacksplashPositionOffset = backsplash.centerOffset;
		let backsplashPositionOffset = rawBacksplashPositionOffset;

		const normalizedAngle = ((panelAngle % 360) + 360) % 360;
		const isBackPanel = Math.abs(normalizedAngle - 180) < 1;
		const isRightPanel = Math.abs(normalizedAngle - 270) < 1;

		if (isBackPanel || isRightPanel) {
			backsplashPositionOffset = -backsplashPositionOffset;
		}

		// Position backsplash on the outside edge of the countertop (toward the overhang)
		const depthOffsetMagnitude = ((panelDepth / 2) + panelOverhang) - (backsplashDepth / 2);

		const angleRad = (panelAngle * Math.PI) / 180;

		const depthOffsetX = Math.sin(angleRad) * depthOffsetMagnitude;
		const depthOffsetZ = Math.cos(angleRad) * depthOffsetMagnitude;

		const widthOffsetX = Math.cos(angleRad) * backsplashPositionOffset;
		const widthOffsetZ = -Math.sin(angleRad) * backsplashPositionOffset;

		const backsplashX = panelCenter.x + depthOffsetX + widthOffsetX;
		const backsplashZ = panelCenter.z + depthOffsetZ + widthOffsetZ;

		const backsplashVectors = convertRect2Vectors({
			layer: Layer.Panel,
			center: { x: 0, y: 0 },
			width: backsplash.size.width,
			height: backsplashDepth,
			angle: 0
		});

		return <g
			key={backsplash.id}
			type="backsplash-panel"
			transform={`translate(${backsplashX}, ${backsplashZ}) rotate(${-panelAngle})`}
		>
			{ svgPolygonFromVectors(
				backsplashVectors,
				{
					key: backsplash.id,
					fill: backsplashFill,
					stroke: 'black',
					strokeWidth: '0.1',
					opacity: backsplashOpacity,
					...addContextMenu({ menuKey: BlueprintInstanceType.Backsplash, id: backsplash.id })
				}
			) }
		</g>;
	}).filter((element): element is React.ReactElement => element !== null);
};
